import { Component } from '@theme/component';
import { fetchConfig, debounce, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /** @type {boolean} */
  #requestInFlight = false;

  /** @type {{ id: string, quantity: number, action: string } | null} */
  #pendingConfig = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Finds the cart item key from a quantity selector event target.
   * Walks up from the input to find the closest [data-cart-key] container.
   * @param {Node} target
   * @returns {string | null}
   */
  #getCartKeyFromTarget(target) {
    const el = target instanceof Element ? target : target.parentElement;
    if (!el) return null;
    const container = el.closest('[data-cart-key]');
    return container?.getAttribute('data-cart-key') || null;
  }

  /**
   * Finds the cart item row by its key.
   * @param {string} key
   * @returns {HTMLTableRowElement | undefined}
   */
  #findRowByKey(key) {
    return this.refs.cartItemRows.find((row) => row.dataset.key === key);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity } = event.detail;
    const key = this.#getCartKeyFromTarget(event.target);

    if (!key) return;

    if (quantity === 0) {
      return this.#removeByKey(key);
    }

    this.#enqueueUpdate({
      id: key,
      quantity,
      action: 'change',
    });

    const lineItemRow = this.#findRowByKey(key);
    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles remove button click. Called from template via on:click.
   * Reads data-cart-key from the button element.
   * @param {Event} event
   */
  onLineItemRemoveByKey(event) {
    const button = event?.target instanceof Element
      ? event.target.closest('[data-cart-key]')
      : null;
    const key = button?.getAttribute('data-cart-key');
    if (!key) return;

    this.#removeByKey(key);
  }

  /**
   * Removes a cart item by its key with animation.
   * @param {string} key
   */
  #removeByKey(key) {
    const cartItemRowToRemove = this.#findRowByKey(key);

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Animate rows out visually — morph will handle final DOM cleanup
    rowsToRemove.forEach((row) => {
      if (prefersReducedMotion()) {
        row.style.display = 'none';
        return;
      }
      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
    });

    this.#enqueueUpdate({
      id: key,
      quantity: 0,
      action: 'clear',
    });
  }

  /**
   * Enqueues a cart update. If a request is in-flight, stores as pending
   * and executes after the current one completes.
   * @param {{ id: string, quantity: number, action: string }} config
   */
  #enqueueUpdate(config) {
    if (this.#requestInFlight) {
      this.#pendingConfig = config;
      return;
    }

    this.#executeUpdate(config);
  }

  /**
   * Executes the cart update fetch using item key (id) instead of line index.
   * @param {{ id: string, quantity: number, action: string }} config
   */
  #executeUpdate(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#requestInFlight = true;
    this.#disableCartItems();

    const { id, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      id,
      quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(id, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#requestInFlight = false;
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);

        if (this.#pendingConfig) {
          const next = this.#pendingConfig;
          this.#pendingConfig = null;
          this.#executeUpdate(next);
        } else {
          this.#enableCartItems();
        }
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {string} id - The cart item key.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (id, parsedResponseText) => {
    const row = this.refs.cartItemRows.find((r) => r.dataset.key === id);
    const quantityInput = row?.querySelector('input[data-cart-line]');

    if (!quantityInput) return;

    /** @type {HTMLInputElement} */ (quantityInput).value = /** @type {HTMLInputElement} */ (quantityInput).defaultValue;

    // Find error container within the same row context
    const errorRow = row?.nextElementSibling;
    if (errorRow?.classList.contains('cart-items__error-row')) {
      const errorText = errorRow.querySelector('.cart-item__error-text');
      if (errorText) errorText.textContent = window.cpErrorUA ? window.cpErrorUA(parsedResponseText.errors) : parsedResponseText.errors;
      errorRow.classList.remove('hidden');
    }
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
